import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, ArrowLeft, Mail, RefreshCw } from 'lucide-react';

const TokenVerification = ({ email, onBack, onVerify }) => {
  const [token, setToken] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const inputRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(prev => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  // Focus management for input fields
  const handleInputChange = (index, value) => {
    // Only allow numeric input
    if (!/^\d*$/.test(value)) return;

    const newToken = [...token];
    newToken[index] = value;
    setToken(newToken);

    // Auto-focus next input or submit if last digit is filled
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    // Auto-submit when last digit is filled
    if (index === 5 && value) {
      handleTokenSubmit(newToken.join(''));
    }
  };

  // Handle backspace to move cursor back
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !token[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // Token verification submit handler
  const handleTokenSubmit = async (completeToken) => {
    setIsSubmitting(true);
    setError(false);
    setErrorMessage('');

    try {
        // Make sure to specify credentials: 'include' to send cookies with your request
        const response = await fetch('/auth/verify-otp', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json', // Set appropriate headers
            },
            credentials: 'include', // Crucial for sending cookies with the request
            body: JSON.stringify({ token: completeToken }), // Include the token in the request body
        });
        
        const data = await response.json(); // Parse JSON response
        //console.log(data);
        
        if (data.success) {
          // API call failed, throw an error
            setToken(['', '', '', '', '', '']);
            //console.log(data.redirect_uri || "OTP verified successfully!");
            window.location.href = data.redirect_uri || "/landing";           
            setIsSubmitting(false);
          
          } else if (data.error) {
              // API call was successful
              throw new Error(data.error || 'Invalid otp');
          }
        
    } catch (error) {
        //console.error(error);
        setErrorMessage(error.message || 'An unexpected error occurred. Please try again later.');
        setError(true);
        setIsSubmitting(false);
        
        // Hide the error after 5 seconds
        setTimeout(() => {
            setError(false);
            setErrorMessage('');
        }, 5000);
    }
};

const handleResendOTP = async () => {
  try {
      // Make an API call to resend the OTP
      const response = await fetch('/auth/resend-otp', {
          method: 'POST',
          credentials: 'include', // Crucial for sending HTTP-only cookies
          headers: {
              'Content-Type': 'application/json' // Optional, but a good practice for clarity
          }
      });

      const data = await response.json(); // Parse JSON response
      //console.log(data.error);
      
      if (data.success) {
          // API call failed, throw an error
          alert(data.message || "OTP resent successfully!");
          
      } else if (data.error) {
          // API call was successful
          throw new Error(data.error);
      }
  } catch (error) {
      // Handle any errors (network, JSON parsing, API errors)
      //console.error(error); // Log the full error for debugging
      setErrorMessage(error.message || 'An unexpected error occurred. Please try again later.'); 
      setError(true);
      setIsSubmitting(false);

      // Hide the error after 5 seconds
      setTimeout(() => {
          setError(false);
          setErrorMessage('');
      }, 5000);
  }
};


  // Reset token input
  const resetToken = () => {
    setToken(['', '', '', '', '', '']);
    inputRefs.current[0].focus();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white">Verify Your Identity</h2>
        <p className="text-gray-400">
          We've sent a 6-digit verification code to <span className="text-white">{email}</span>
        </p>
      </div>

      <div className="flex justify-center space-x-2 mb-6">
        {token.map((digit, index) => (
          <input
            key={index}
            type="text"
            maxLength="1"
            value={digit}
            ref={(el) => (inputRefs.current[index] = el)}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="w-12 h-12 text-center text-xl 
              bg-gray-900/50 border border-gray-700 
              rounded-lg focus:ring-2 focus:ring-teal-500 
              focus:border-transparent transition-all"
          />
        ))}
      </div>

      {error && (
        <div
          className="error-message text-center text-red-400 mb-4"
        >
          {errorMessage}
        </div>
      )}

      <div className="space-y-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleTokenSubmit(token.join(''))}
          disabled={isSubmitting || token.some(digit => digit === '')}
          className="w-full py-2.5 bg-gradient-to-r from-teal-600 to-coral-600 
            text-white rounded-lg transition-all 
            flex items-center justify-center space-x-2
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ShieldCheck size={18} />
          <span>{isSubmitting ? 'Verifying...' : 'Verify Code'}</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={resetToken}
          className="w-full py-2.5 border border-gray-700 
            text-white rounded-lg hover:bg-gray-800 
            transition-all flex items-center justify-center space-x-2"
        >
          <span>Reset Code</span>
        </motion.button>
      </div>

      <div className="text-center text-sm mt-4">
        <div className="flex justify-center items-center space-x-2">
          <span>Didn't receive the code?</span>
          <button 
            onClick={handleResendOTP}
            disabled={!canResend}
            className={`
              flex items-center space-x-1 
              ${canResend 
                ? 'text-teal-400 hover:text-teal-300' 
                : 'text-gray-500 cursor-not-allowed'
              }
            `}
          >
            <RefreshCw size={14} />
            <span>
              {canResend 
                ? 'Resend Code' 
                : `Resend in ${resendCountdown}s`
              }
            </span>
          </button>
        </div>

        <button 
          onClick={onBack} 
          className="mt-4 text-teal-400 hover:text-teal-300 flex items-center justify-center space-x-2 w-full"
        >
          <ArrowLeft size={16} />
          <span>Back to Previous Step</span>
        </button>
      </div>
    </motion.div>
  );
};

export default TokenVerification;
